import * as queryString from "query-string";

import {
    AnalyticsEventPayload,
    createAndFireEvent
} from "@atlaskit/analytics-next";
import {
    UIEventPayload,
    OperationalEventPayload
} from "@atlassiansox/analytics-web-client";

import { ProductKeysType } from "@/libraries/signup/bxpkit-core/src/model/analytics";

import { Cookie } from "../../utils/cookie";

import { AnalyticsState } from "./analytics-state";
import { AtlCohort } from "./cohort";
import { Guid } from "./guid";
import { Helpers } from "./helpers";
import { Identity } from "./identity";
import { Tracking } from "./tracking";
import { TrelloUrlScrubber } from "./trello-url-scrubber";

const attributeWhiteList = ["atlCohort", "featureFlags"];

const ATL_USER_HASH = "atlUserHash";

type Identifiers = {
    anonymousId: string | null;
    atlassianAccountId: string | null;
    crossDomainId?: string | number | null;
    optimizelyEndUserId: string | null;
    trelloUserId?: string | null;
};

export type AnalyticsData = {
    atl_first_visit?: string;
    sn_id?: string;
    _atl_path?: string;
    _sio?: string;
    actionSubjectId?: string;
    atlCohort?: string | AtlCohort;
    atlEventId?: string;
    atlJSVersion?: string | null;
    atlXid?: string | number;
    browserLanguages?: string | readonly string[]; // TODO - should technically remain as readonly string []
    bxpGatewayRequestId?: string;
    campaign?: {
        "name"?: string;
        "campaign"?: string;
        "sfdc-campaign_id"?: string;
        "medium"?: string;
        "source"?: string;
        "content"?: string;
        "term"?: string;
    };
    category?: string;
    className?: string;
    clientSessionId?: string;
    cmsPageVariant?: string | unknown;
    page_channel?: string;
    page_anchor?: string;
    page_subAnchor?: string;
    page_aj_keyword?: string;
    onboardingJourney?: object;
    data?: string | unknown;
    destinationFlag?: string;
    event?: string;
    eventColumn?: string;
    eventComponent?: string;
    eventContainer?: string;
    href?: string;
    identifiers?: string | Identifiers;
    isBot?: boolean;
    isUniform?: boolean;
    compositionStatusCode?: number;
    label?: string | null;
    loginUserDetected?: string;
    marketoId?: string;
    name?: string | null;
    pageViewId?: string | null;
    screenNameOverride?: string;
    trackEventName?: string;
    wrapperName?: string;
    writeKey?: string | null;
    form?: string;

    // signup data attributes
    activateProducts?: boolean;
    cartedProducts?: (string | null)[];
    cloudId?: string;
    eventType?: string;
    exception?: string;
    originLibrary?: string;
    originId?: string;
    originProduct?: string;
    originIdGenerated?: string;
    originMalformed?: true;
    performanceDuration?: number;
    productAttributes?: Record<
        string,
        string | null | undefined | (string | null)[]
    >;
    productEdition?: string;
    productKey?: ProductKeysType[];
    productPlatform?: string;
    productsOffered?: string[];
    products?: unknown[];
    schemaVersion?: number;
    selectedSiteId?: string;
    suiteName?: string;
    signupUrl?: string;
    type?: string;
    value?: string;
};

export type EnrichedAnalyticsData = AnalyticsData & {
    activeGroups?: string;
    cookiesEnabled?: string | boolean;
    isMarketingEvent?: string | boolean;
    page_canonical_url?: string;
    page_domain?: string;
    page_path?: string;
    page_referrer_domain?: string;
    page_referrer_path?: string;
    page_referrer_subdomain?: string;
    page_referrer_url?: string;
    page_referrer_url_params_json?: string;
    page_subdomain?: string;
    page_title?: string;
    page_url?: string;
    page_url_parameters?: string;
    page_url_params_json?: string;
};

/**
 * Module for GASv3 tracking events.
 * @module GASv3
 */
export class GASv3 {
    public static isMarketingEvent?: boolean;
    /**
     * Sends a UI event to GASv3
     * @function sendUIEvent
     * @param {string} name
     * @param {object} data
     * @param {object} skipEnrichment - whether to add additional data
     */
    public static sendUIEvent(
        name: string,
        data: AnalyticsData,
        skipEnrichment?: boolean
    ) {
        if (!skipEnrichment) {
            data = GASv3.enrichEventData(data);
        }
        data["trackEventName"] = name;
        data.href = data.href ? Helpers.maskURL(data.href) : undefined;
        data.signupUrl = data.signupUrl
            ? Helpers.maskURL(data.signupUrl)
            : undefined;
        const source = GASv3.setSource(data);
        const uiEvent: UIEventPayload = {
            // this will likely change
            action: data["event"] || "clicked",
            actionSubject: data["eventComponent"] || "component",
            actionSubjectId: data["actionSubjectId"] || undefined,
            source: source,
            attributes: data
        };

        this._createAndFireEvent("ui", uiEvent);
    }

    /**
     * Sends a page view operational event to GASv3
     * @param data - The data to send to GASv3
     * @param skipEnrichment - Whether to skip enrichment
     */
    public static sendOperationalEvent(
        data: AnalyticsData,
        skipEnrichment?: boolean
    ) {
        if (!skipEnrichment) {
            data = GASv3.enrichEventData(data);
        }

        data.href = data.href ? Helpers.maskURL(data.href) : undefined;
        data.signupUrl = data.signupUrl
            ? Helpers.maskURL(data.signupUrl)
            : undefined;
        const source = GASv3.setSource(data);

        if (AnalyticsState.analyticsOptOut) {
            const fieldsToRemove = ["atlCohort", "name", "atlXid", "marketoId"];
            const dataRecord = data as unknown as Record<string, unknown>;
            fieldsToRemove.forEach(function (key) {
                delete dataRecord[key];
            });
        }

        const operationalEvent: OperationalEventPayload = {
            // this will likely change
            action: data["event"] || "",
            actionSubject: data["eventComponent"] || "",
            actionSubjectId: data["actionSubjectId"] || undefined,
            source: source,
            attributes: data,
            ...(AnalyticsState.analyticsOptOut
                ? {
                      excludedProperties: {
                          anonymousId: true,
                          authenticatedUserId: true,
                          userId: true,
                          xid: true
                      }
                  }
                : {})
        };

        this._createAndFireEvent("operational", operationalEvent);
    }

    public static _createAndFireEvent(
        eventType: "ui" | "screen" | "operational" | "track",
        payload: AnalyticsEventPayload
    ) {
        if (!AnalyticsState.createAnalyticsEvent) {
            return;
        }

        // When user has opted out, only allow operational events (e.g. errors, diagnostics)
        if (AnalyticsState.analyticsOptOut && eventType !== "operational") {
            return;
        }

        const payloadToSend: AnalyticsEventPayload = { ...payload, eventType };

        createAndFireEvent("atl-global")(payloadToSend)(
            AnalyticsState.createAnalyticsEvent
        );
    }

    /**
     * Sends a Screen event to GASv3
     * @function sendScreenEvent
     * @param {string} name
     * @param {object} data - GASv3 screen event attributes
     *                 data.screenNameOverride - special data attribute that overrides the screen name for the event, instead of the default 'page'.
     *                                           If used, this attribute will be stripped from the data before sending to GASv3.
     * @param {object} skipEnrichment - whether to add additional data
     */
    public static sendScreenEvent(
        name: string | null,
        data: AnalyticsData,
        skipEnrichment?: boolean
    ) {
        if (!skipEnrichment) {
            data = GASv3.enrichEventData(data);
        }
        data.href = data.href ? Helpers.maskURL(data.href) : undefined;
        data.signupUrl = data.signupUrl
            ? Helpers.maskURL(data.signupUrl)
            : undefined;
        data["name"] = name;
        const screenName =
            (typeof data !== "undefined" && data["screenNameOverride"]) ||
            "page";
        if (typeof data === "object") {
            delete data["screenNameOverride"];
        }

        this._createAndFireEvent("screen", {
            name: screenName,
            attributes: data
        });

        GASv3.sendIdentityEvent();
    }

    /**
     *
     * @function sendIdentityEvent
     */
    public static sendIdentityEvent() {
        // load the identity hash cookie
        const atlUserHash = Cookie.readCookie(ATL_USER_HASH);
        // get the user identifiers
        const identifiers = GASv3.getUserIdentifiers();
        // calculate the data hash of the known identifiers
        const calculatedHash = Guid.guidHashCode(
            JSON.stringify(identifiers)
        ).toString();
        if (!atlUserHash || atlUserHash !== calculatedHash) {
            // save new hash and send UI event
            Cookie.writeCookie(ATL_USER_HASH, calculatedHash, 3650, true);
            const data: AnalyticsData = {
                event: "identified",
                eventComponent: "user",
                identifiers: identifiers,
                writeKey: AnalyticsState.writeKey
            };
            GASv3.sendUIEvent("", data, true);
        }
    }

    public static getUserIdentifiers(): Identifiers {
        return {
            anonymousId: AnalyticsState.anonymousId,
            atlassianAccountId: Identity.getAtlassianUserId(),
            crossDomainId: Tracking.xid,
            optimizelyEndUserId: null,
            trelloUserId: Identity.getTrelloUserId()
        };
    }

    public static setSource(data: AnalyticsData) {
        let set = false;
        let source = "";

        if (data["eventContainer"]) {
            source += data["eventContainer"].split(" ")[0];
            set = true;
        }
        if (data["eventColumn"]) {
            source += data["eventColumn"].split(" ")[0];
            set = true;
        }
        if (!set) {
            source = "none";
        }
        return source;
    }

    // fix for hot-99118
    public static getReferrerURL(ref: string) {
        if (Identity.isReferrerTrello()) {
            const referrer = new URL(ref);
            return (
                referrer.hostname +
                TrelloUrlScrubber.scrubUrl(referrer.pathname, "")
            );
        }
        return ref;
    }

    /**
     * This function is to specifically add data needed in Socrates but not provided by default in GASv3 like it is for Segment
     * @function enrichEventData
     * @param {object} inputData  // a dictionary of data to enrich
     */
    // eslint-disable-next-line complexity
    public static enrichEventData(inputData: unknown) {
        let data: AnalyticsData = {};

        if (typeof inputData !== "object" || inputData === null) {
            const dataValue = inputData;
            if (typeof dataValue !== "undefined") {
                data.data = dataValue;
            }
        } else {
            data = inputData as AnalyticsData;
        }

        const newData = JSON.parse(
            JSON.stringify(data)
        ) as EnrichedAnalyticsData;
        newData.writeKey = AnalyticsState.writeKey;
        newData.isMarketingEvent = GASv3.isMarketingEvent;

        let page;
        let ref: URL | null;
        let page_canonical_url;

        try {
            // convert all passed in values to string if they are currently objects
            Object.keys(newData).forEach((inputKey) => {
                const key = inputKey as keyof EnrichedAnalyticsData;
                if (
                    newData[key] &&
                    typeof newData[key] === "object" &&
                    !Array.isArray(newData[key]) &&
                    !attributeWhiteList.includes(key)
                ) {
                    // @ts-ignore
                    newData[key] = JSON.stringify(newData[key]);
                }
            });

            const href = GASv3.getLocation();
            page = href ? new URL(Helpers.maskURL(href)) : null;
            const referredByJiraDotCom =
                page?.searchParams.get("referer") === "jira.com";
            ref = referredByJiraDotCom
                ? new URL("https://www.jira.com")
                : document.referrer
                ? new URL(Helpers.maskURL(document.referrer))
                : null;

            page_canonical_url = Helpers.maskURL(Helpers.getCanonicalUrl());

            newData.page_title = newData.page_title || document.title || "";
            newData.page_domain = page ? page.hostname || "" : "";
            newData.page_subdomain = page ? page.hostname || "" : "";

            // referrer attributes
            newData.page_referrer_domain = ref ? ref.hostname || "" : "";
            newData.page_referrer_subdomain = ref ? ref.hostname || "" : "";

            if (Identity.isReferrerTrello()) {
                // fix for hot-99118
                newData.page_canonical_url = page
                    ? TrelloUrlScrubber.scrubUrl(page.href, page.origin) || ""
                    : "";
                newData.page_url = page
                    ? TrelloUrlScrubber.scrubUrl(page.href, page.origin) || ""
                    : "";
                newData.page_path = page
                    ? TrelloUrlScrubber.scrubUrl(
                          page.pathname,
                          new URL(page.href).pathname
                      ) || ""
                    : "";
                newData.page_url_parameters = page
                    ? TrelloUrlScrubber.scrubUrl(page.search, "")
                    : "";
                newData.page_url_params_json = page
                    ? JSON.stringify(
                          queryString.parse(
                              TrelloUrlScrubber.scrubUrl(
                                  page.search,
                                  new URL(page.origin).pathname
                              )
                          )
                      )
                    : "";
                newData.page_referrer_url_params_json = ref
                    ? JSON.stringify(
                          queryString.parse(
                              TrelloUrlScrubber.scrubUrl(
                                  ref.search,
                                  new URL(ref.origin).pathname
                              )
                          )
                      )
                    : "";

                newData.page_referrer_path = ref
                    ? TrelloUrlScrubber.scrubUrl(
                          ref.pathname,
                          new URL(ref.origin).pathname
                      ) || ""
                    : "";
                newData.page_referrer_url = ref
                    ? ref.origin +
                          TrelloUrlScrubber.scrubUrl(
                              ref.pathname,
                              new URL(ref.origin).pathname
                          ) || ""
                    : "";
            } else {
                newData.page_canonical_url = page_canonical_url;
                newData.page_url = page ? page.href || "" : "";
                newData.page_path = page ? page.pathname || "" : "";
                newData.page_url_parameters = page ? page.search || "" : "";
                newData.page_url_params_json = page
                    ? JSON.stringify(queryString.parse(page.search))
                    : "";
                newData.page_referrer_url_params_json = ref
                    ? JSON.stringify(queryString.parse(ref.search))
                    : "";

                newData.page_referrer_path = ref ? ref.pathname || "" : "";
                newData.page_referrer_url = ref ? ref.href || "" : "";
            }

            newData.cookiesEnabled = window.navigator
                ? window.navigator.cookieEnabled
                : "unknown";
            newData.activeGroups = window.OnetrustActiveGroups
                ? window.OnetrustActiveGroups
                : "";
        } catch (e) {
            // fix for hot-99118
            const additionalData = {
                error: e instanceof Error ? e.message : JSON.stringify(e),
                href: location ? location.href : "no location",
                referrer: document
                    ? document.referrer
                        ? GASv3.getReferrerURL(document.referrer)
                        : "no referrer"
                    : "no document"
            };

            Helpers.sendWrapperLoadError(
                "GASv3 enrichEventData error",
                JSON.stringify(additionalData)
            );
        }

        return newData;
    }

    public static getLocation() {
        return location.href;
    }
}
